import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

function CrosswordGrid({ grid, userAnswers, activeWord, onCellChange, onWordSelect }) {
  const gridRef = useRef(null);

  const getCellValue = (row, col) => {
    return userAnswers[`${row}-${col}`] || '';
  };

  const isCorrect = (row, col) => {
    const cell = grid.grid[row][col];
    if (cell.isBlack) return false;
    
    const userValue = getCellValue(row, col).toUpperCase();
    return userValue === cell.letter.toUpperCase();
  };

  const isInActiveWord = (row, col) => {
    if (!activeWord) return false;
    return activeWord.cells.some(cell => cell.row === row && cell.col === col);
  };

  const handleCellClick = (row, col) => {
    const cell = grid.grid[row][col];
    if (cell.isBlack) return;

    // Find words that contain this cell
    const wordsAtCell = grid.words.filter(word => 
      word.cells.some(cell => cell.row === row && cell.col === col)
    );

    if (wordsAtCell.length > 0) {
      // If there's an active word and this cell is part of it, switch to the other word
      const currentWordIndex = wordsAtCell.findIndex(word => word === activeWord);
      const nextWordIndex = (currentWordIndex + 1) % wordsAtCell.length;
      onWordSelect(wordsAtCell[nextWordIndex]);
    }
  };

  const handleCellChange = (row, col, value) => {
    if (value.length <= 1 && /^[a-zA-ZäöüÄÖÜß]*$/.test(value)) {
      onCellChange(row, col, value);
      
      // Auto-advance to next cell in active word
      if (activeWord && value) {
        const currentCellIndex = activeWord.cells.findIndex(
          cell => cell.row === row && cell.col === col
        );
        
        if (currentCellIndex >= 0 && currentCellIndex < activeWord.cells.length - 1) {
          const nextCell = activeWord.cells[currentCellIndex + 1];
          const nextInput = gridRef.current?.querySelector(
            `input[data-row="${nextCell.row}"][data-col="${nextCell.col}"]`
          );
          nextInput?.focus();
        }
      }
    }
  };

  const handleKeyDown = (e, row, col) => {
    if (e.key === 'Backspace' && !getCellValue(row, col) && activeWord) {
      // Move to previous cell if current is empty
      const currentCellIndex = activeWord.cells.findIndex(
        cell => cell.row === row && cell.col === col
      );
      
      if (currentCellIndex > 0) {
        const prevCell = activeWord.cells[currentCellIndex - 1];
        const prevInput = gridRef.current?.querySelector(
          `input[data-row="${prevCell.row}"][data-col="${prevCell.col}"]`
        );
        prevInput?.focus();
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
    >
      <h2 className="text-xl font-semibold text-primary mb-4 font-rubik">Kreuzworträtsel</h2>
      
      <div className="overflow-auto">
        <div
          ref={gridRef}
          className="crossword-grid mx-auto"
          style={{
            gridTemplateColumns: `repeat(${grid.size}, 1fr)`,
            width: 'fit-content',
            minWidth: '300px',
            maxWidth: '600px'
          }}
        >
          {grid.grid.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              if (cell.isBlack) {
                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className="grid-cell black aspect-square"
                    style={{ minHeight: '40px' }}
                  />
                );
              }

              const cellValue = getCellValue(rowIndex, colIndex);
              const correct = isCorrect(rowIndex, colIndex);
              const inActiveWord = isInActiveWord(rowIndex, colIndex);

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className="relative"
                >
                  {cell.number && (
                    <div className="cell-number">{cell.number}</div>
                  )}
                  <input
                    type="text"
                    value={cellValue}
                    onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                    data-row={rowIndex}
                    data-col={colIndex}
                    className={`
                      grid-cell aspect-square w-full text-center font-semibold text-lg font-rubik
                      ${correct ? 'correct' : ''}
                      ${inActiveWord ? 'active-word' : ''}
                    `}
                    style={{ minHeight: '40px' }}
                    maxLength={1}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default CrosswordGrid;